import clsx from 'clsx';
import { ReactComponent as DIAProduct } from 'assets/images/dia_product.svg';
import { ReactComponent as CradlepointProduct } from 'assets/images/cradlepoint_product.svg';
import starlinkBg from 'assets/images/starfield_simulation.gif';
import { useCallback, useState } from 'react';
import {
  OfferingType,
  TagType,
} from 'screens/AccessExpressV3/accessExpress.types';
import DiaOrCradlepoint from '../alternatives/DiaOrCradlepoint';
import Starlink from '../alternatives/Starlink';
import GlowingBorder from '../../BaseComponents/GlowingBorder';
import OfferBadge from './OfferBadge';
import CuratedOfferDetails from './CuratedOfferDetails';
import { TermType } from 'screens/SelectServices/utils';
import { ProductType } from 'screens/SelectServices/schemas';
import { observer } from 'mobx-react-lite';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import { SelectedPackages } from 'context/SingleServiceManagerContext';

const OfferCard = observer(
  ({
    tag,
    isLoading,
    onAddToCart,
    offering,
    serviceSummaryData,
  }: {
    tag: TagType;
    isLoading: boolean;
    selectedOffer: string | null;
    onAddToCart: (tag?: string, mns?: SelectedPackages) => void;
    offering: OfferingType | undefined;
    serviceSummaryData?: (ProductType & { term: TermType }) | undefined;
  }) => {
    const { isItemAlreadyInCart, hasCircuitLimitReached } = useCartContext();

    const isSelected = isItemAlreadyInCart(offering?.id as string);

    const isStarlink = !isLoading && tag === 'Best offer' && !offering;
    const isDia = !isLoading && tag === 'Premium choice' && !offering;
    const isCradlepoint = !isLoading && tag === 'Lowest price' && !offering;

    const isAlternativeOffer = isStarlink || isDia || isCradlepoint;

    const isDisabled = hasCircuitLimitReached('Broadband', offering?.id ?? '');

    const [mns, setMNS] = useState<SelectedPackages>({
      essential: true,
      enhanced: false,
    });

    const handleMNSChange = useCallback(() => {
      if (serviceSummaryData?.formal_broadband_pricing) {
        setMNS((prev) => ({ ...prev, essential: !prev.essential }));
      }
      if (serviceSummaryData?.formal_dia_pricing) {
        setMNS((prev) => ({ ...prev, enhanced: !prev.enhanced }));
      }
    }, [serviceSummaryData]);

    const getOfferContent = useCallback(() => {
      if (isStarlink) return <Starlink />;
      // alternative offers
      if (isDia || isCradlepoint) {
        return (
          <DiaOrCradlepoint
            carrier={isDia ? 'Dia' : 'Cradlepoint E100'}
            price={isDia ? '$389.90' : '$35.99'}
            image={isDia ? DIAProduct : CradlepointProduct}
            type={isDia ? 'dia' : 'cradlepoint'}
          />
        );
      }
      // curated offers
      if (offering)
        return (
          <CuratedOfferDetails
            tag={tag}
            offering={offering}
            isSelected={isSelected}
            onAddToCart={(tag) => onAddToCart(tag, mns)}
            serviceSummaryData={serviceSummaryData}
            mns={mns}
            onMNSChange={handleMNSChange}
          />
        );
      return null;
    }, [
      handleMNSChange,
      isCradlepoint,
      isDia,
      isSelected,
      isStarlink,
      mns,
      offering,
      onAddToCart,
      serviceSummaryData,
      tag,
    ]);

    return (
      <div
        className={clsx(
          'relative z-0 min-h-[200px] min-w-0 overflow-visible sm:w-full',
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (!isAlternativeOffer && !isSelected && !isDisabled) {
            onAddToCart(tag, mns);
          }
        }}
      >
        {tag === 'Best offer' && <GlowingBorder />}
        <div
          className={clsx(
            clsx(
              'flex h-full w-full flex-col gap-4 rounded-t-[4px] border border-[#47556980] p-6',
              tag !== 'Best offer' && '!rounded-t-none !border-t-0',
              tag === 'Best offer' && 'border-transparent shadow-xl',
              tag === 'Best offer' &&
                (isSelected
                  ? 'bg-background-base-surface-2'
                  : 'bg-background-base-surface-1'),
              tag !== 'Best offer' && isSelected && 'bg-[#9796F30F]',
              tag === 'Premium choice' && 'border-r-0',
            ),
          )}
          style={
            isStarlink
              ? {
                  backgroundImage: `url(${starlinkBg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  imageRendering: 'pixelated',
                }
              : {}
          }
        >
          <OfferBadge
            tag={tag}
            isLoading={isLoading}
            isAlternativeOffer={isAlternativeOffer}
          />
          {getOfferContent()}
        </div>
      </div>
    );
  },
);

export default OfferCard;
